import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Divider
} from '@mui/material';
import { McTag } from "@maersk-global/mds-react-wrapper/components-core/mc-tag";

const maerskFontFamily = '"Maersk Text", -apple-system, BlinkMacSystemFont, "Microsoft JhengHei", "Microsoft Yahei", "微软雅黑", STXihei, "华文细黑", sans-serif';

const OverviewTab = ({ changeData, getStateAppearance, getRiskAppearance, getTypeAppearance, getCategoryAppearance }) => {
    const details = changeData?.change_details;
    if (!details) return null;

    return (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Single Card with all content */}
                <Card sx={{ mb: 3, border: '1px solid #d9d9d9', boxShadow: 'none', borderRadius: '10px' }}>
                    <CardContent sx={{ padding: '24px' }}>
                        {/* Change Details Section */}
                        <Typography variant="h6" gutterBottom sx={{ fontFamily: maerskFontFamily, color: '#333' }}>
                            Change Details
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
                            <Box>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    CMDB CI
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: maerskFontFamily, fontWeight: 400, mt: 0.5 }}>
                                    {details.cmdb_ci || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    CMDB CI Environment
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: maerskFontFamily, fontWeight: 400, mt: 0.5 }}>
                                    {details.cmdb_ci_environment || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    Start Date
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: maerskFontFamily, fontWeight: 400, mt: 0.5 }}>
                                    {details.start_date || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    End Date
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: maerskFontFamily, fontWeight: 400, mt: 0.5 }}>
                                    {details.end_date || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    Requested By
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: maerskFontFamily, fontWeight: 400, mt: 0.5 }}>
                                    {details.requested_by || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    Opened By
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: maerskFontFamily, fontWeight: 400, mt: 0.5 }}>
                                    {details.opened_by || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    Assignment Group
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: maerskFontFamily, fontWeight: 400, mt: 0.5 }}>
                                    {details.assignment_group || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    Assigned To
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: maerskFontFamily, fontWeight: 400, mt: 0.5 }}>
                                    {details.assigned_to || 'N/A'}
                                </Typography>
                            </Box>
                        </Box>

                        {/* First Divider */}
                        <Divider sx={{ my: 3 }} />

                        {/* Scrollable Content Sections */}
                        {/* Short Description */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500, mb: 1 }}>
                                Short Description
                            </Typography>
                            {(() => {
                                const value = details.short_description || 'No short description available';
                                const isLong = value.length > 70;
                                return isLong ? (
                                    <textarea
                                        value={value}
                                        disabled
                                        rows={Math.min(6, Math.ceil(value.length / 70))}
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            resize: "vertical",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={value}
                                        disabled
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                );
                            })()}
                        </Box>

                        {/* Description */}
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500, mb: 1 }}>
                                Description
                            </Typography>
                            {(() => {
                                const value = details.description || 'No description available';
                                const isLong = value.length > 70;
                                return isLong ? (
                                    <textarea
                                        value={value}
                                        disabled
                                        rows={Math.min(6, Math.ceil(value.length / 70))}
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            resize: "vertical",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={value}
                                        disabled
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                );
                            })()}
                        </Box>

                    </CardContent>
                </Card>

                {/* Planning Card */}
                <Card sx={{ mb: 3, border: '1px solid #d9d9d9', boxShadow: 'none', borderRadius: '10px' }}>
                    <CardContent sx={{ padding: '24px' }}>
                        <Typography variant="h6" gutterBottom sx={{ fontFamily: maerskFontFamily, color: '#333' }}>
                            Planning
                        </Typography>

                        {/* Justification */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500, mb: 1 }}>
                                Justification
                            </Typography>
                            {(() => {
                                const value = details.justification || 'No justification available';
                                const isLong = value.length > 70;
                                return isLong ? (
                                    <textarea
                                        value={value}
                                        disabled
                                        rows={Math.min(6, Math.ceil(value.length / 70))}
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            resize: "vertical",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={value}
                                        disabled
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                );
                            })()}
                        </Box>

                        {/* Implementation Plan */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500, mb: 1 }}>
                                Implementation Plan
                            </Typography>
                            {(() => {
                                const value = details.implementation_plan || 'No implementation plan available';
                                const isLong = value.length > 70;
                                return isLong ? (
                                    <textarea
                                        value={value}
                                        disabled
                                        rows={Math.min(6, Math.ceil(value.length / 70))}
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            resize: "vertical",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={value}
                                        disabled
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                );
                            })()}
                        </Box>

                        {/* Risk Impact Analysis */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500, mb: 1 }}>
                                Risk Impact Analysis
                            </Typography>
                            {(() => {
                                const value = details.risk_impact_analysis || 'No risk impact analysis available';
                                const isLong = value.length > 70;
                                return isLong ? (
                                    <textarea
                                        value={value}
                                        disabled
                                        rows={Math.min(6, Math.ceil(value.length / 70))}
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            resize: "vertical",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={value}
                                        disabled
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                );
                            })()}
                        </Box>

                        {/* Backout Plan */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500, mb: 1 }}>
                                Backout Plan
                            </Typography>
                            {(() => {
                                const value = details.backout_plan || 'No backout plan available';
                                const isLong = value.length > 70;
                                return isLong ? (
                                    <textarea
                                        value={value}
                                        disabled
                                        rows={Math.min(6, Math.ceil(value.length / 70))}
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            resize: "vertical",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={value}
                                        disabled
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                );
                            })()}
                        </Box>

                        {/* Test Plan */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500, mb: 1 }}>
                                Test Plan
                            </Typography>
                            {(() => {
                                const value = details.test_plan || 'No test plan available';
                                const isLong = value.length > 70;
                                return isLong ? (
                                    <textarea
                                        value={value}
                                        disabled
                                        rows={Math.min(6, Math.ceil(value.length / 70))}
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            resize: "vertical",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={value}
                                        disabled
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                );
                            })()}
                        </Box>

                        {/* Reason */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500, mb: 1 }}>
                                Reason
                            </Typography>
                            {(() => {
                                const value = details.reason || 'No reason provided';
                                const isLong = value.length > 70;
                                return isLong ? (
                                    <textarea
                                        value={value}
                                        disabled
                                        rows={Math.min(6, Math.ceil(value.length / 70))}
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            resize: "vertical",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={value}
                                        disabled
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                );
                            })()}
                        </Box>

                        {/* Approval History */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500, mb: 1 }}>
                                Approval History
                            </Typography>
                            {(() => {
                                const value = details.approval_history || 'No approval history available';
                                const isLong = value.length > 70;
                                return isLong ? (
                                    <textarea
                                        value={value}
                                        disabled
                                        rows={Math.min(6, Math.ceil(value.length / 70))}
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            resize: "vertical",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={value}
                                        disabled
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                );
                            })()}
                        </Box>

                        {/* Close Notes */}
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500, mb: 1 }}>
                                Close Notes
                            </Typography>
                            {(() => {
                                const value = details.close_notes || 'No close notes available';
                                const isLong = value.length > 70;
                                return isLong ? (
                                    <textarea
                                        value={value}
                                        disabled
                                        rows={Math.min(6, Math.ceil(value.length / 70))}
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            resize: "vertical",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={value}
                                        disabled
                                        style={{
                                            fontSize: "1rem",
                                            borderRadius: "8px",
                                            border: "1px solid #e0e0e0",
                                            background: "#f7f7f7",
                                            padding: "8px 12px",
                                            color: "#222",
                                            outline: "none",
                                            width: "100%",
                                            boxSizing: "border-box",
                                            fontFamily: maerskFontFamily
                                        }}
                                    />
                                );
                            })()}
                        </Box>

                    </CardContent>
                </Card>
            </Box>

            <Box sx={{ width: { xs: '100%', md: '33.33%' }, flexShrink: 0 }}>
                {/* Quick Stats Card */}
                <Card sx={{ mb: 3, border: '1px solid #d9d9d9', boxShadow: 'none', borderRadius: '10px'  }}>
                    <CardContent sx={{ padding: '24px' }}>
                        <Typography variant="h6" gutterBottom sx={{ fontFamily: maerskFontFamily, color: '#333' }}>
                            Quick Stats
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily }}>
                                    State
                                </Typography>
                                <McTag
                                    label={details?.state || 'Unknown'}
                                    appearance={getStateAppearance(details?.state)}
                                    fit="small"
                                />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily }}>
                                    Risk
                                </Typography>
                                {details.risk && (
                                    <McTag
                                        label={details.risk}
                                        appearance={getRiskAppearance(details.risk)}
                                        fit="small"
                                    />
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily }}>
                                    Type
                                </Typography>
                                {details.type && (
                                    <McTag
                                        label={details.type.charAt(0).toUpperCase() + details.type.slice(1)}
                                        appearance={getTypeAppearance(details.type)}
                                        fit="small"
                                    />
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily }}>
                                    Category
                                </Typography>
                                {details.category && (
                                    <McTag
                                        label={details.category}
                                        appearance={getCategoryAppearance(details.category)}
                                        fit="small"
                                    />
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily }}>
                                    Change Request Urgent
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    {details.u_change_request_urgent || 'N/A'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily }}>
                                    Close Code
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    {details.close_code || 'N/A'}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* Additional Details Card */}
                <Card sx={{ mb: 3, border: '1px solid #d9d9d9', boxShadow: 'none', borderRadius: '10px' }}>
                    <CardContent sx={{ padding: '24px' }}>
                        <Typography variant="h6" gutterBottom sx={{ fontFamily: maerskFontFamily, color: '#333' }}>
                            Additional Details
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Horizontal layout fields */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily }}>
                                    Is Change Solution Tested
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    {details.u_is_change_solution_tested || 'N/A'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily }}>
                                    Project Programme
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    {details.u_project_programme || 'N/A'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily }}>
                                    CAB Required
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    {details.cab_required || 'N/A'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily }}>
                                    CAB Date
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    {details.cab_date || 'N/A'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily }}>
                                    Work Start
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    {details.work_start || 'N/A'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily }}>
                                    Work End
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    {details.work_end || 'N/A'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily }}>
                                    On Hold
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    {details.on_hold || 'N/A'}
                                </Typography>
                            </Box>
                            
                            {/* Vertical layout fields */}
                            <Box>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    On Hold Reason
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                    fontFamily: maerskFontFamily, 
                                    fontWeight: 400, 
                                    mt: 0.5,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    {details.on_hold_reason?.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n') || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    Previous Changes
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                    fontFamily: maerskFontFamily, 
                                    fontWeight: 400, 
                                    mt: 0.5,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word'
                                }}>
                                    {details.u_previous_changes || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500 }}>
                                    Watch List
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                    fontFamily: maerskFontFamily, 
                                    fontWeight: 400, 
                                    mt: 0.5,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word'
                                }}>
                                    {details.watch_list || 'N/A'}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default OverviewTab;
